import { computed, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { resolveLocalizedTitle } from "@/composables/useCardData";

// Question card types whose answers are closed (finite codes) and therefore filterable.
export const CLOSED_FILTERABLE_TYPES = [
  "q_single",
  "q_multiple",
  "q_ranking",
  "q_image_choice",
  "q_image_choice_multiple",
  "q_rating",
  "q_slider",
  "q_nps",
  "q_matrix",
  "o_ab",
  "o_cells",
];

export interface FilterChoice {
  value: string;
  label: string;
}

// A leaf maps to one backend question id (matrix item, loop iteration, or item × iteration).
export interface FilterLeaf {
  questionId: string;
  label: string;
}

// One accordion entry per filterable card. Plain cards carry their questionId directly;
// matrix/loop cards expose a flat list of leaves. Choices are shared across leaves.
export interface FilterGroup {
  key: string;
  label: string;
  questionId: string | null;
  children: FilterLeaf[] | null;
  choices: FilterChoice[];
}

interface CardQuestion {
  id: number | string;
  rank?: number;
  item_token?: string | null;
  item_title?: unknown;
  loop_index?: number | null;
  loop_details?: { loop_title?: unknown } | null;
}

interface CardSpecs {
  question?: unknown;
  answers?: { title?: unknown }[];
  populations?: { title?: unknown }[];
  cells?: { title?: unknown }[];
  items?: { token: string; title?: unknown }[];
  matrix_type?: string;
  rating_limits?: { min?: number; max?: number };
  slider_limits?: { min?: number; max?: number };
  recode_type?: string | null;
}

export interface FilterableCard {
  id?: number;
  token?: string;
  type: string;
  name?: string;
  specs?: CardSpecs;
  questions?: CardQuestion[];
}

interface LoopIteration {
  index: number;
  title: unknown;
  question_id: number | string;
}

function matrixItems(card: FilterableCard): { token: string; title?: unknown }[] {
  if (card.type === "q_matrix") return card.specs?.items || [];
  const seen = new Set<string>();
  return (card.questions || [])
    .filter((q) => !!q.item_token && !seen.has(q.item_token) && !!seen.add(q.item_token))
    .map((q) => ({ token: q.item_token as string, title: q.item_title }));
}

function buildLoopIterations(questions: CardQuestion[] | undefined): LoopIteration[] {
  const withLoop = (questions || []).filter((q) => q.loop_index != null);
  if (!withLoop.length) return [];
  const byIndex = new Map<number, CardQuestion[]>();
  for (const q of withLoop) {
    const index = q.loop_index as number;
    if (!byIndex.has(index)) byIndex.set(index, []);
    (byIndex.get(index) as CardQuestion[]).push(q);
  }
  return [...byIndex.entries()]
    .sort(([a], [b]) => a - b)
    .map(([index, group]) => {
      const first = group.reduce((a, b) => ((a.rank ?? 0) <= (b.rank ?? 0) ? a : b));
      return { index, title: first.loop_details?.loop_title, question_id: first.id };
    });
}

function choiceOptionsForCard(card: FilterableCard, locale: string | null): FilterChoice[] {
  const type = card.type;
  const s = card.specs || {};

  const fromAnswers = (answers?: { title?: unknown }[]): FilterChoice[] =>
    (answers || []).map((a, i) => ({
      value: String(i + 1),
      label: resolveLocalizedTitle(a.title, locale) || String(i + 1),
    }));
  const fromRange = (min: number, max: number): FilterChoice[] => {
    const out: FilterChoice[] = [];
    for (let n = min; n <= max; n++) out.push({ value: String(n), label: String(n) });
    return out;
  };

  if (
    ["q_single", "q_multiple", "q_ranking", "q_image_choice", "q_image_choice_multiple", "q_hidden"].includes(type)
  ) {
    return fromAnswers(s.answers);
  }
  if (type === "o_ab") return fromAnswers(s.populations);
  if (type === "o_cells") return fromAnswers(s.cells);
  if (type === "q_matrix") {
    if (s.matrix_type === "single" || s.matrix_type === "multiple") return fromAnswers(s.answers);
    if (s.matrix_type === "rating") return fromRange(s.rating_limits?.min ?? 1, s.rating_limits?.max ?? 10);
    if (s.matrix_type === "nps") return fromRange(0, 10);
    return [];
  }
  if (type === "q_rating") return fromRange(s.rating_limits?.min ?? 1, s.rating_limits?.max ?? 10);
  if (type === "q_slider") return fromRange(s.slider_limits?.min ?? 0, s.slider_limits?.max ?? 10);
  if (type === "q_nps") return fromRange(0, 10);
  return [];
}

function isFilterableCard(card: FilterableCard): boolean {
  const isClosedFilterable = CLOSED_FILTERABLE_TYPES.includes(card.type);
  const isContactAttribute =
    card.type === "q_hidden" && !card.specs?.recode_type && (card.specs?.answers?.length || 0) > 0;
  const isRecodeWithAnswers =
    card.type === "q_hidden" && !!card.specs?.recode_type && (card.specs?.answers?.length || 0) > 0;
  return isClosedFilterable || isContactAttribute || isRecodeWithAnswers;
}

export function useFilterableQuestions(cards: Ref<FilterableCard[]>) {
  const { analysisLocale } = storeToRefs(useSurveyLocalesStore());

  const filterGroups = computed<FilterGroup[]>(() => {
    const locale: string | null = analysisLocale.value?.token || null;
    const result: FilterGroup[] = [];

    for (const card of cards.value) {
      if (!isFilterableCard(card)) continue;

      const cardName = card.name || "";
      const cardTitle = resolveLocalizedTitle(card.specs?.question, locale);
      const label = cardName && cardTitle ? `${cardName} - ${cardTitle}` : cardTitle || cardName;
      const key = card.token || String(card.id ?? label);
      const choices = choiceOptionsForCard(card, locale);

      const iterations = buildLoopIterations(card.questions);
      const items = matrixItems(card);
      const children: FilterLeaf[] = [];

      if (items.length) {
        for (const item of items) {
          const itemTitle = resolveLocalizedTitle(item.title, locale) || item.token;
          if (iterations.length) {
            for (const it of iterations) {
              const q = (card.questions || []).find(
                (x) => x.item_token === item.token && x.loop_index === it.index,
              );
              if (!q) continue;
              const iterTitle = resolveLocalizedTitle(it.title, locale) || `Iteration ${it.index}`;
              children.push({ questionId: String(q.id), label: `${itemTitle} (${iterTitle})` });
            }
          } else {
            const q = (card.questions || []).find((x) => x.item_token === item.token);
            if (!q) continue;
            children.push({ questionId: String(q.id), label: itemTitle });
          }
        }
      } else if (iterations.length) {
        for (const it of iterations) {
          const iterTitle = resolveLocalizedTitle(it.title, locale) || `Iteration ${it.index}`;
          children.push({ questionId: String(it.question_id), label: iterTitle });
        }
      }

      if (children.length) {
        result.push({ key, label, questionId: null, children, choices });
      } else {
        const qid = card.questions?.[0]?.id;
        if (!qid) continue;
        result.push({ key, label, questionId: String(qid), children: null, choices });
      }
    }

    return result;
  });

  return { filterGroups };
}
